import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import LocationFilter from '../components/LocationFilter';
import { useLockersContext } from '../contexts/LockersContext';
import { Api } from '../utils/Api';
import { ErrorComponent } from '../components/ErrorComponent';
import AppHeader from '../components/AppHeader';
import { useTrackingContext } from '../contexts/TrackingContext';
import { LocalStorageFavouriteLocker, Locker, FavouriteLockerType } from '../data/LockerDataTypes';
import { trackPromise } from 'react-promise-tracker';
import TrackingComponent from '../components/TrackingComponent';
import { FilterModel, FilterRequest } from '../utils/filterAssistent';
import { useSpecificLockerActionContext } from '../contexts/SpecificLockerActionContext';
import { load } from '@fingerprintjs/fingerprintjs';
import { useFiltersContext } from '../contexts/FiltersContext';
import FiltersContainer from '../components/FiltersContainer';
import { MapCenterComponent } from '../components/MapCenterComponent';
import { BYPASS_HOST_VALIDATION, DEVELOPMENT } from '../Settings';
import LoadingIndicator from '../components/LoadingIndicator';
import { useRecaptchaContext } from '../contexts/RecaptchaContext';
import RecaptchaComponent from '../components/RecaptchaComponent';

function Welcome() {
    const SDK_ASSUMED_MISSING_AFTER_TIMEOUT_IN_MS = 200;
    const { lang } = useParams() as any;
    const { t, i18n } = useTranslation();

    let [isSdkDetected, setIsSdkDetected] = useState<boolean|undefined>(undefined);
    let [invalidHost, setInvalidHost] = useState(false);
    let [favouriteLockerIdChecked, setFavouriteLockerIdChecked] = useState(false);

    
    const {recaptchaValidated} = useRecaptchaContext();
    
    const {
        lockerPluginOptions
    } = useLockersContext();

    const {
        setDomain,
        setUserFingerprint
    } = useTrackingContext();

    const {
        favouriteLockers,
        setFavouriteLockers
    } = useSpecificLockerActionContext();

    const {
        filterStrategies
    } = useFiltersContext();

    useEffect(() => {
        detectSdk();
        load()
            .then(agent => agent.get())
            .then(fingerprint => {
                setUserFingerprint(fingerprint.visitorId);
            })
            .catch(() => {
                setUserFingerprint(undefined);
            })
            .finally(() => {
                subscribeHostValidation();
                postLockerPluginReady();
                SetFavouriteLockerDetails();
            });

        if (lang !== undefined) {
            i18n.changeLanguage(lang);
        }
        setFilters();
    }, []);
    
    const detectSdk = () => trackPromise(
        new Promise(resolve => setTimeout(()=>{
            setIsSdkDetected(x => x === undefined ? (DEVELOPMENT === true) : x);
            resolve(1);
        }, SDK_ASSUMED_MISSING_AFTER_TIMEOUT_IN_MS))
    );

    const setFilters = (): void => {
        if (lockerPluginOptions.filters) {
            lockerPluginOptions.filters?.forEach(filter => {
                const queryParamFilterKey: string = Object.keys(filter)[0];
                const queryParamFilterValue = filter[queryParamFilterKey as keyof typeof filter]

                filterStrategies.filter(x=>x.name==queryParamFilterKey)[0].implementation.value=queryParamFilterValue;
            });
        }
    }

    const hostValidationHandler = (event: MessageEvent<string>) => {
        if (event.data === 'ValidateThisHost') {
            setIsSdkDetected(true);
            try {
                if (BYPASS_HOST_VALIDATION) {
                    return;
                }
                let host = new URL(event.origin).host;
                setDomain(host);
                trackPromise(Api.Get().IsHostValid(lockerPluginOptions.clientId, host).then(result => {
                    setInvalidHost(result === false)
                }))
            }
            catch {
                setInvalidHost(true)
            }
        }
    };
    
    const subscribeHostValidation = (): void => {
        window.addEventListener("message", hostValidationHandler);
    }

    const postLockerPluginReady = () => window?.parent?.postMessage('lockerPluginReady','*');
    
    const SetFavouriteLockerDetails = (): void => {
        if (lockerPluginOptions.favLockerId === -1) {
            setFavouriteLockerIdChecked(true);
            return;
        }

        trackPromise(Api.Get().GetLockers(new FilterRequest<Locker>({}, new FilterModel<Locker>({ lockerId: lockerPluginOptions.favLockerId })))
            .then(res => {
                if (res && res.count > 0) {
                    let favouriteLocker = {
                        lockerId: res.data[0].lockerId,
                        lat: res.data[0].lat,
                        lng: res.data[0].lng,
                        type: FavouriteLockerType.Unknown
                    } as LocalStorageFavouriteLocker;
                    if (favouriteLockers.findIndex(x => x.lockerId == favouriteLocker.lockerId) === -1) {
                        setFavouriteLockers([...favouriteLockers, favouriteLocker]);
                    }
                }
            }
            )).then(() => {setFavouriteLockerIdChecked(true);})
    };

    return (
    <>
       
        <TrackingComponent />
            {!invalidHost && isSdkDetected && favouriteLockerIdChecked && 
            <>
                <RecaptchaComponent/>
                {recaptchaValidated && 
                <>
                    <LoadingIndicator />
                    <Container fluid className="max-height gx-0">
                        <FiltersContainer/>
                        <Row className="max-height gx-2 gy-2">
                                <Col md={4} className={'text-left'}>
                                    <AppHeader />
                                    <LocationFilter />
                                </Col>
                                <Col md={8} className={'mt-1'}>
                                <MapCenterComponent />
                                </Col>
                        </Row>
                    </Container>
                </>
                }
            </>

            || (invalidHost && isSdkDetected && <ErrorComponent msg={t('clientNotFoundErrorMsg')} />)
            || (isSdkDetected===false && <ErrorComponent msg={t('sdkNotFoundErrorMsg')} />)
            }
    </>);
}
export default Welcome;
